<script setup lang="ts">
/**
 * Knitting kiosk — ONE screen, four steps of the same layout.
 *
 * The step bar always shows the current instruction, so the terminal never looks
 * like a different app between steps:
 *
 *   1. idle    — "Scan your card to create new record" (card reader / typed card)
 *   2. product — pick the production order (product name + colours, no MO code)
 *   3. matrix  — colour/size matrix of that order + Save output
 *   4. result  — the batch number to write on the sticker, then it resets to 1
 *
 * Saving posts one `POST /production-execution/completions` per filled cell and
 * the returned lot number becomes the basket batch number. The unfinished
 * submissions are kept in a local outbox keyed by `client_event_id`, so a retry
 * replays the exact same request and the backend stays idempotent.
 */
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import { ArrowLeft, Check, ChevronLeft, ChevronRight, Delete, History, Home, Maximize2, Minimize2, Moon, Printer, RefreshCw, ScanLine, Settings, Sun, Timer, X } from 'lucide-vue-next'
import { RouterLink } from 'vue-router'
import { useShopfloorApi } from '../composables/useShopfloorApi'
import {
  buildKioskMatrix,
  clampKioskIdleTimeout,
  clampKioskQuantity,
  firstOperationStageCodes,
  firstStageOnlyRows,
  groupWorkQueue,
  kioskClockLabel,
  kioskDateLabel,
  kioskEntries,
  kioskCardUidVariants,
  kioskIdleTimeoutMs,
  kioskJournalFromBasket,
  kioskJournalLineLots,
  kioskJournalLineSummary,
  kioskJournalTotals,
  kioskNumpadInput,
  kioskPaginate,
  kioskRecentJournal,
  mergeWorkQueue,
  newRfidCapture,
  parseStageCodes,
  rfidKey,
  KIOSK_IDLE_TIMEOUT_MAX_S,
  KIOSK_IDLE_TIMEOUT_MIN_S,
  KIOSK_IDLE_TIMEOUT_S,
  KIOSK_NUMPAD_ROWS,
  type JournalEntry,
  type KioskLabels,
  type KioskNumpadKey,
  type KioskProductGroup,
} from '../types/knittingKiosk'
import type {
  BasketResult,
  CreateBasketRequest,
  EmployeeOption,
  KnittingShift,
  WarehouseAgentOption,
  WorkQueueItem,
} from '../types/shopfloor'

type Phase = 'idle' | 'product' | 'matrix' | 'result'

interface KioskWorker {
  id: number
  name: string
  code: string
  subtitle: string
  photoUrl: string | null
}

interface KioskConfig {
  /**
   * Manual stage-code override. Empty means "detect automatically from the open
   * production orders" — every order carries its own BOM stage codes, so a
   * hardcoded code such as "knitting" would return an empty work queue.
   */
  stage_codes: string[]
  warehouse_agent_id: number | null
  /** Bearer token of this kiosk device (`kiosk_devices.token_hash` pre-image). */
  token: string
  /**
   * Inactivity countdown (seconds) before the worker is logged out. Editable in
   * the terminal setup; the basket ticket never logs out on its own.
   */
  idle_timeout_s: number
}

/**
 * One pending basket submission. The body is stored verbatim so a retry replays
 * the identical `client_event_id` and the backend answers with the same basket.
 */
interface OutboxEntry {
  client_event_id: string
  payload: CreateBasketRequest
  /** Kept beside the payload because the API body must stay verbatim. */
  product_name?: string
  status: 'pending' | 'retryable' | 'needs_review'
  error?: string
}

const CONFIG_KEY = 'shopfloor.knittingKiosk.config'
const OUTBOX_KEY = 'shopfloor.knittingKiosk.outbox'
const JOURNAL_KEY = 'shopfloor.knittingKiosk.journal'
const DEVICE_KEY = 'shopfloor.knittingKiosk.deviceId'
/** Orders inspected when the terminal detects its stage codes on its own. */
const STAGE_ORDER_SCAN_LIMIT = 40
/** How long a detected stage-code set is reused before it is looked up again. */
const STAGE_CACHE_MS = 60000
/**
 * Terminal token of the shopfloor knitting kiosk. Card scans authenticate with
 * this device token instead of the signed-in operator session; it can be
 * overridden per device in the terminal setup (or at build time with
 * `VITE_SHOPFLOOR_KIOSK_TOKEN`).
 */
const DEFAULT_KIOSK_TOKEN =
  String(import.meta.env.VITE_SHOPFLOOR_KIOSK_TOKEN ?? '').trim() || '123456'
/** Header clock refresh — one second keeps the displayed time exact. */
const CLOCK_TICK_MS = 1000
/** Events that count as the worker still using the terminal. */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'input', 'touchstart', 'wheel'] as const

const { t, locale } = useI18n()
const auth = useAuthStore()
const {
  listWorkQueue,
  createProductionBasket,
  closeProductionBasket,
  getProductionBasket,
  listProductionBaskets,
  listSemiFinishedWarehouses,
  listEmployees,
  listCurrentProductionOrders,
  listStages,
  workerCardLogin,
} = useShopfloorApi()

// --- device storage helpers ---------------------------------------------

function readStored<T>(key: string, fallback: T): T {
  try {
    const raw = globalThis.localStorage?.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function writeStored(key: string, value: unknown): void {
  try {
    globalThis.localStorage?.setItem(key, JSON.stringify(value))
  } catch {
    // Private mode or a full quota — the kiosk keeps working from memory.
  }
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

function randomToken(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
}

/** Stable per-terminal identifier; the prefix of every idempotency key. */
function terminalId(): string {
  let id = ''
  try {
    id = globalThis.localStorage?.getItem(DEVICE_KEY) ?? ''
  } catch {
    id = ''
  }
  if (!id) {
    id = `kiosk-${randomToken().slice(0, 8)}`
    try {
      globalThis.localStorage?.setItem(DEVICE_KEY, id)
    } catch {
      // ignore — a per-session id is still unique enough for retries
    }
  }
  return id
}

function newClientEventId(): string {
  return `${terminalId()}:${randomToken()}`
}

function errorStatus(error: unknown): number | null {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status
  return typeof status === 'number' ? status : null
}

function localDate(): string {
  const now = new Date()
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** ISO 8601 with the device offset, as required by `device_event_time`. */
function localDateTime(): string {
  const now = new Date()
  const offset = -now.getTimezoneOffset()
  const sign = offset >= 0 ? '+' : '-'
  const absolute = Math.abs(offset)
  return `${localDate()}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}${sign}${pad(
    Math.floor(absolute / 60),
  )}:${pad(absolute % 60)}`
}

function shiftForNow(): KnittingShift {
  const hour = new Date().getHours()
  return hour >= 6 && hour < 18 ? 'day' : 'night'
}

// --- state ---------------------------------------------------------------

const phase = ref<Phase>('idle')
const worker = ref<KioskWorker | null>(null)
const shift = ref<KnittingShift>(shiftForNow())
const setupOpen = ref(false)
const warehouses = ref<WarehouseAgentOption[]>([])
const config = ref<KioskConfig>({
  stage_codes: [],
  warehouse_agent_id: null,
  token: DEFAULT_KIOSK_TOKEN,
  idle_timeout_s: KIOSK_IDLE_TIMEOUT_S,
})
/** Raw text of the stage-code field in the terminal setup. */
const stageCodeInput = ref('')
/** Stage codes detected from the open production orders. */
const autoStageCodes = ref<string[]>([])
/** Effective codes: the manual override when set, otherwise the detected ones. */
const stageCodes = ref<string[]>([])
/** First-operation stage codes per order id, used to filter the merged queue. */
const firstStageByOrder = ref<Record<string, string[]>>({})
let stageCodesDiscoveredAt = 0

const cardUid = ref('')
const cardInput = ref<HTMLInputElement | null>(null)
const codeInput = ref<HTMLInputElement | null>(null)
const codeMode = ref(false)
const employeeCode = ref('')
const employees = ref<EmployeeOption[]>([])
const loginBusy = ref(false)
const loginError = ref('')

const queue = ref<WorkQueueItem[]>([])
const queueLoading = ref(false)
const queueError = ref('')
const search = ref('')
const activeProduct = ref<KioskProductGroup | null>(null)

const quantities = ref<Record<string, string>>({})
const cellWarning = ref('')
const saving = ref(false)
const saveError = ref('')

const results = ref<BasketResult[]>([])

// --- terminal journal ----------------------------------------------------

/**
 * Baskets this terminal saved. The backend has no basket list endpoint, and a
 * terminal must also work offline, so the last 24 h are kept on the device.
 */
const journal = ref<JournalEntry[]>([])
const journalOpen = ref(false)
const journalFilter = ref<'mine' | 'all'>('mine')
/** Journal loaded from the basket list endpoint (every terminal). */
const journalServer = ref<JournalEntry[]>([])
const journalLoading = ref(false)
/** True when the basket list could not be reached: fall back to this device. */
const journalOffline = ref(false)
const outbox = ref<OutboxEntry[]>([])
let idleTimer: number | null = null
/** Epoch ms of the pending logout; drives the session countdown in the bar. */
const idleDeadline = ref<number | null>(null)

// --- terminal header (date + clock) --------------------------------------

/** Ticks once a second; the header shows the date and the live clock only. */
const now = ref(new Date())
let clockTimer: number | null = null
const clockLabel = computed(() => kioskClockLabel(now.value))
const dateLabel = computed(() => kioskDateLabel(now.value, String(locale.value)))

// --- session countdown (header) ------------------------------------------
// The top bar shows how long the worker stays signed in. It re-derives from
// `idleDeadline` on every clock tick (1 s), so it always matches the real timer.
const idleSeconds = computed(() => {
  if (idleDeadline.value === null) return null
  return Math.max(0, Math.ceil((idleDeadline.value - now.value.getTime()) / 1000))
})
const idleCountdown = computed(() => {
  const seconds = idleSeconds.value
  if (seconds === null) return ''
  return `${Math.floor(seconds / 60)}:${pad(seconds % 60)}`
})

// --- fullscreen (kiosk top bar) ------------------------------------------
// Browsers without the Fullscreen API (iOS Safari in particular) hide the
// button instead of showing a control that cannot work.
const canFullscreen = computed(
  () => typeof document !== 'undefined' && typeof document.documentElement.requestFullscreen === 'function',
)
const isFullscreen = ref(false)

function syncFullscreen(): void {
  isFullscreen.value = typeof document !== 'undefined' && !!document.fullscreenElement
}

async function toggleFullscreen(): Promise<void> {
  try {
    if (document.fullscreenElement) {
      await document.exitFullscreen()
    } else {
      await document.documentElement.requestFullscreen()
    }
  } catch {
    // The browser/device refused (kiosk policy, iframe without allowfullscreen).
  }
  syncFullscreen()
}

const canManage = computed(() => (auth.user?.permissions ?? []).includes('production_orders.manage'))
const warehouseName = computed(
  () => warehouses.value.find((warehouse) => warehouse.id === config.value.warehouse_agent_id)?.name ?? '',
)
const shifts = ['day', 'night'] as const
/**
 * Caption under each shift icon. Deliberately locale-independent: the floor uses
 * the short Latin transliteration "Den" / "Noch" on every terminal.
 */
const SHIFT_CAPTIONS: Record<KnittingShift, string> = { day: 'Den', night: 'Noch' }
/** Chips/labels of the stage codes this terminal consumes. */
const stageSummary = computed(() =>
  stageCodes.value.length ? stageCodes.value.join(', ') : t('shopfloor.knittingKiosk.stageNone'),
)
const autoStageSummary = computed(() =>
  autoStageCodes.value.length ? autoStageCodes.value.join(', ') : t('shopfloor.knittingKiosk.stageNone'),
)

// --- step bar copy -------------------------------------------------------

const stepInstruction = computed(() => {
  switch (phase.value) {
    case 'product':
      return t('shopfloor.knittingKiosk.stepPick')
    case 'matrix':
      return t('shopfloor.knittingKiosk.stepEnter')
    case 'result':
      return t('shopfloor.knittingKiosk.stepSaved')
    default:
      return t('shopfloor.knittingKiosk.stepScan')
  }
})

const stepHint = computed(() => {
  switch (phase.value) {
    case 'product':
      return t('shopfloor.knittingKiosk.productsSubtitle')
    case 'matrix':
      return t('shopfloor.knittingKiosk.stepEnterHint')
    case 'result':
      return t('shopfloor.knittingKiosk.stepSavedHint')
    default:
      return t('shopfloor.knittingKiosk.loginSubtitle')
  }
})

const kioskLabels = computed<KioskLabels>(() => ({
  noColour: t('shopfloor.knittingKiosk.matrixNoColour'),
  noSize: t('shopfloor.knittingKiosk.matrixNoSize'),
}))

// --- queue ---------------------------------------------------------------

const products = computed(() => groupWorkQueue(queue.value, kioskLabels.value))

const filteredProducts = computed(() => {
  const term = search.value.trim().toLowerCase()
  if (!term) return products.value
  return products.value.filter(
    (group) =>
      group.productName.toLowerCase().includes(term) ||
      group.colors.some((colour) => colour.toLowerCase().includes(term)),
  )
})

function openProduct(group: KioskProductGroup): void {
  activeProduct.value = group
  quantities.value = {}
  cellWarning.value = ''
  saveError.value = ''
  closeNumpad()
  phase.value = 'matrix'
}

// --- matrix --------------------------------------------------------------

const matrix = computed(() => buildKioskMatrix(activeProduct.value?.rows ?? [], kioskLabels.value))

// --- horizontal matrix scrolling -----------------------------------------
// A product with many colours makes the matrix wider than the screen. The
// container scrolls horizontally (touch swipe / shift+wheel) and the arrows are
// only rendered while there is something to scroll in that direction, so they
// double as a "you can swipe here" hint.
const matrixScroll = ref<HTMLElement | null>(null)
const matrixScrollState = ref({ left: false, right: false })

function updateMatrixScroll(): void {
  const el = matrixScroll.value
  if (!el) {
    matrixScrollState.value = { left: false, right: false }
    return
  }
  const max = el.scrollWidth - el.clientWidth
  matrixScrollState.value = {
    left: el.scrollLeft > 4,
    right: max > 4 && el.scrollLeft < max - 4,
  }
}

function scrollMatrix(direction: -1 | 1): void {
  const el = matrixScroll.value
  if (!el) return
  el.scrollBy({ left: direction * Math.max(160, Math.round(el.clientWidth * 0.8)), behavior: 'smooth' })
}

watch(phase, (value) => {
  if (value === 'matrix') void nextTick(updateMatrixScroll)
})
watch(
  () => matrix.value.colors.length,
  () => void nextTick(updateMatrixScroll),
)

const rowsByWorkItem = computed(() => {
  const map: Record<string, WorkQueueItem> = {}
  for (const row of activeProduct.value?.rows ?? []) map[row.work_item_id] = row
  return map
})

const entries = computed(() => kioskEntries(activeProduct.value?.rows ?? [], quantities.value, kioskLabels.value))

const totalOutput = computed(() => entries.value.reduce((sum, entry) => sum + entry.quantity, 0))

function onQuantityInput(workItemId: string, event: Event): void {
  const input = event.target as HTMLInputElement
  const maximum = Number(rowsByWorkItem.value[workItemId]?.remaining_quantity) || 0
  const { value, clamped } = clampKioskQuantity(input.value, maximum)
  input.value = value
  quantities.value = { ...quantities.value, [workItemId]: value }
  cellWarning.value = clamped ? t('shopfloor.knittingKiosk.matrixMax', { max: maximum }) : ''
}

// --- on-screen number pad (touch terminals) ------------------------------

/** `work_item_id` of the cell whose number pad is open (empty = closed). */
const numpadCell = ref('')

const numpadKeyRows = KIOSK_NUMPAD_ROWS

/** Size/colour/remaining of the cell the number pad drives. */
const numpadTarget = computed(() => {
  if (!numpadCell.value) return null
  for (const line of matrix.value.rows) {
    for (const cell of line.cells) {
      if (cell.workItemId === numpadCell.value) {
        return { size: line.size, colour: cell.color, remaining: cell.remaining }
      }
    }
  }
  return null
})

/**
 * Touch terminals open the pad as soon as a quantity field is focused — there is
 * no toggle icon any more, a tap on the input is the only way in. Desktop
 * keyboards keep typing normally (the pad stays closed).
 */
function isTouchTerminal(): boolean {
  if (typeof window === 'undefined') return false
  const coarse = typeof window.matchMedia === 'function'
    && (window.matchMedia('(any-pointer: coarse)').matches || window.matchMedia('(pointer: coarse)').matches)
  return coarse || (globalThis.navigator?.maxTouchPoints ?? 0) > 0
}

function onQuantityFocus(workItemId: string): void {
  if (!isTouchTerminal()) return
  numpadCell.value = workItemId
}

function closeNumpad(): void {
  numpadCell.value = ''
}

/** Terminal setup (gear) — the keypad must not sit behind the overlay. */
function openSetup(): void {
  closeNumpad()
  setupOpen.value = true
}

function numpadKeyLabel(key: KioskNumpadKey): string {
  if (key === 'back') return t('shopfloor.knittingKiosk.numpadBack')
  return key
}

function numpadPress(key: KioskNumpadKey): void {
  const workItemId = numpadCell.value
  if (!workItemId) return
  const maximum = Number(rowsByWorkItem.value[workItemId]?.remaining_quantity) || 0
  const { value, clamped } = kioskNumpadInput(quantities.value[workItemId] ?? '', key, maximum)
  quantities.value = { ...quantities.value, [workItemId]: value }
  cellWarning.value = clamped ? t('shopfloor.knittingKiosk.matrixMax', { max: maximum }) : ''
}

function clearAll(): void {
  quantities.value = {}
  cellWarning.value = ''
  saveError.value = ''
  closeNumpad()
}

// --- outbox --------------------------------------------------------------

function persistOutbox(): void {
  writeStored(OUTBOX_KEY, outbox.value)
}

const pendingCount = computed(() => outbox.value.length)

// --- journal -------------------------------------------------------------

/** Only the last 24 h, and by default only the logged-in worker's baskets. */
const journalSource = computed(() => (journalOffline.value ? journal.value : journalServer.value))
const recentJournal = computed(() =>
  kioskRecentJournal(journalSource.value, {
    now: now.value,
    employeeId: journalFilter.value === 'mine' ? worker.value?.id ?? null : null,
  }),
)
const journalTotals = computed(() => kioskJournalTotals(recentJournal.value))

// --- journal paging ------------------------------------------------------

/** Rows per journal page; the list is filtered client-side, hence local paging. */
const JOURNAL_PER_PAGE = 10
const journalPage = ref(1)
const journalPaged = computed(() =>
  kioskPaginate(recentJournal.value, journalPage.value, JOURNAL_PER_PAGE),
)

function setJournalPage(page: number): void {
  journalPage.value = page
  bumpIdleTimer()
}

// A smaller filtered list could leave the pager beyond the last page.
watch(journalFilter, () => {
  journalPage.value = 1
})

/** Keep the stored journal inside the 24 h window so it cannot grow forever. */
function persistJournal(): void {
  journal.value = kioskRecentJournal(journal.value, { now: new Date() })
  writeStored(JOURNAL_KEY, journal.value)
}

function recordJournalEntries(baskets: BasketResult[], productName: string): void {
  const savedAt = new Date().toISOString()
  const created: JournalEntry[] = baskets.map((basket) => ({
    basketCode: basket.basket_code,
    basketId: basket.id,
    employeeId: worker.value?.id ?? null,
    employeeName: worker.value?.name ?? '',
    productName,
    stageCode: basket.stage_code,
    status: basket.status,
    savedAt,
    totalQuantity: Number(basket.completed_quantity) || 0,
    lines: (basket.lines ?? []).map((line) => ({
      color: line.color_name ?? '—',
      size: line.size_label ?? '—',
      quantity: Number(line.completed_quantity) || 0,
      lotNumber: line.lot_number,
    })),
  }))

  journal.value = [...journal.value, ...created]
  persistJournal()
}

/** `YYYY-MM-DD` shifted by whole days, used as the list endpoint's `date_from`. */
function localDateOffset(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() + days)
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * Load the last 24 h of baskets from the server.
 *
 * The endpoint filters on `output_date` (a date), so the request asks for
 * yesterday onwards and the exact 24 h window is applied on `created_at` by
 * `kioskRecentJournal`. When the terminal cannot reach the server it keeps
 * showing its own device journal instead of an empty screen.
 */
async function loadJournal(): Promise<void> {
  journalLoading.value = true
  try {
    const collected: JournalEntry[] = []
    let page = 1
    let totalPages = 1
    do {
      const response = await listProductionBaskets({
        date_from: localDateOffset(-1),
        page,
        per_page: 100,
      })
      collected.push(...response.items.map((item) => kioskJournalFromBasket(item)))
      totalPages = Math.max(1, response.meta.total_pages)
      page += 1
    } while (page <= totalPages && page <= 3)

    journalServer.value = collected
    journalOffline.value = false
  } catch {
    // Keep working offline: the device journal is still on screen.
    journalOffline.value = true
  } finally {
    journalLoading.value = false
  }
}

/** `HH:MM` for the journal rows (seconds only matter on the header clock). */
function journalTime(savedAt: string): string {
  const date = new Date(savedAt)
  return Number.isNaN(date.getTime()) ? '—' : kioskClockLabel(date).slice(0, 5)
}

function openJournal(): void {
  closeNumpad()
  journalPage.value = 1
  journalOpen.value = true
  void loadJournal()
  bumpIdleTimer()
}

/**
 * Send everything currently queued.
 *
 * `force` (the banner's Retry button) also re-sends entries the server rejected
 * earlier: the cause may be fixed in the meantime (plan corrected, stage
 * reopened). Without it a rejected entry would sit in the queue forever with
 * neither a way to send nor to drop it.
 */
async function flushOutbox(options: { force?: boolean } = {}): Promise<void> {
  const queued = options.force
    ? [...outbox.value]
    : outbox.value.filter((entry) => entry.status !== 'needs_review')
  if (!queued.length) return

  const saved: BasketResult[] = []
  const failures: string[] = []

  for (const entry of queued) {
    // Back to "pending" while it is in flight, so the banner reflects the attempt.
    entry.status = 'pending'
    entry.error = undefined
    persistOutbox()
    try {
      const basket = await createProductionBasket(entry.payload)
      try {
        const closed = await closeProductionBasket(basket.id, {
          stage_code: entry.payload.stage_code,
          employee_id: entry.payload.employee_id,
          idempotency_key: `${entry.client_event_id}:auto-close`,
          notes: 'Knitting output saved; advance basket to the next operation.',
        })
        basket.status = closed.status
      } catch (closeCause) {
        // A lost close response leaves the outbox entry pending. On retry the
        // create call replays the original open response, so verify the live
        // basket before treating the close conflict as a real failure.
        const current = await getProductionBasket(basket.id)
        if (current.status !== 'advanced' && current.status !== 'completed') throw closeCause
        basket.status = current.status
      }
      saved.push(basket)
      recordJournalEntries([basket], entry.product_name ?? '')
      outbox.value = outbox.value.filter((item) => item.client_event_id !== entry.client_event_id)
      persistOutbox()
    } catch (cause) {
      const status = errorStatus(cause)
      entry.error = formatApiError(cause)
      // 4xx needs a human decision (over-plan conflict, closed stage, bad data);
      // network/5xx errors are safe to retry with the same client_event_id.
      entry.status = status !== null && status >= 400 && status < 500 ? 'needs_review' : 'retryable'
      failures.push(entry.error)
      persistOutbox()
    }
  }

  if (saved.length) {
    results.value = [...results.value, ...saved]
    phase.value = 'result'
    if (failures.length) saveError.value = t('shopfloor.knittingKiosk.pendingFailed')
  } else if (failures.length) {
    saveError.value = failures[0]
  }
  bumpIdleTimer()
}

/** One guarded send, used by "Save output" and by the queue's Retry button. */
async function sendQueued(options: { force?: boolean } = {}): Promise<void> {
  if (saving.value) return
  saving.value = true
  saveError.value = ''
  try {
    await flushOutbox(options)
  } finally {
    saving.value = false
    // The send itself was the last interaction: restart the logout countdown.
    bumpIdleTimer()
  }
}

/** Total pairs of one unsent submission, for the queue list. */
function pendingQuantity(entry: OutboxEntry): number {
  return entry.payload.rows.reduce((sum, row) => sum + (Number(row.completed_quantity) || 0), 0)
}

/**
 * Drop one unsent submission. Confirmed, because the output is lost for good:
 * nothing was recorded on the server for it.
 */
function cancelOutboxEntry(clientEventId: string): void {
  if (!outbox.value.some((entry) => entry.client_event_id === clientEventId)) return
  if (!window.confirm(t('shopfloor.knittingKiosk.pendingCancelConfirm'))) return

  outbox.value = outbox.value.filter((entry) => entry.client_event_id !== clientEventId)
  persistOutbox()
  if (!outbox.value.length) saveError.value = ''
  bumpIdleTimer()
}

async function saveOutput(): Promise<void> {
  if (saving.value) return
  if (!entries.value.length) {
    saveError.value = t('shopfloor.knittingKiosk.matrixEmpty')
    return
  }
  if (!config.value.warehouse_agent_id) {
    saveError.value = t('shopfloor.knittingKiosk.matrixNoWarehouse')
    setupOpen.value = true
    return
  }
  saveError.value = ''

  // One submission = ONE physical basket: every filled colour/size cell becomes a
  // basket line of the same `POST /production-execution/baskets` call.
  const rows = entries.value.map((entry) => ({
    work_item_id: entry.row.work_item_id,
    completed_quantity: entry.quantity,
    rejected_quantity: 0,
    rework_quantity: 0,
  }))
  const payload: CreateBasketRequest = {
    client_event_id: newClientEventId(),
    production_order_id: activeProduct.value?.orderId ?? entries.value[0].row.production_order_id,
    stage_code: entries.value[0].row.stage_code,
    warehouse_agent_id: Number(config.value.warehouse_agent_id),
    shift: shift.value,
    output_date: localDate(),
    device_event_time: localDateTime(),
    employee_id: worker.value?.id ?? null,
    machine_id: null,
    notes: null,
    rows,
  }

  // Persisted before the first HTTP attempt: a failed send is never lost and a
  // retry reuses the identical idempotency key (the backend then replays the
  // same basket instead of creating a second one).
  outbox.value = [
    ...outbox.value,
    {
      client_event_id: payload.client_event_id,
      payload,
      product_name: activeProduct.value?.productName ?? '',
      status: 'pending',
    },
  ]
  persistOutbox()

  await sendQueued()
}

// --- navigation + inactivity logout --------------------------------------

function stopIdleTimer(): void {
  if (idleTimer !== null) {
    window.clearTimeout(idleTimer)
    idleTimer = null
  }
  idleDeadline.value = null
}

/**
 * (Re)arm the logout countdown. Only an identified worker can be logged out, and
 * the countdown is paused while output is being sent so a slow request never
 * interrupts a save.
 *
 * The basket ticket is deliberately exempt: a freshly saved basket must not log
 * the operator out, so its countdown only returns to the orders list. From there
 * the normal countdown applies again.
 */
function bumpIdleTimer(): void {
  stopIdleTimer()
  if (!worker.value || saving.value) return
  const timeout = kioskIdleTimeoutMs(config.value.idle_timeout_s)
  idleDeadline.value = Date.now() + timeout
  idleTimer = window.setTimeout(() => {
    idleTimer = null
    idleDeadline.value = null
    if (phase.value === 'result') {
      backToProducts()
      return
    }
    resetToScan()
  }, timeout)
}

function backToProducts(): void {
  activeProduct.value = null
  quantities.value = {}
  cellWarning.value = ''
  saveError.value = ''
  // Leaving the ticket must not show it again after the next basket is saved.
  results.value = []
  closeNumpad()
  phase.value = 'product'
  bumpIdleTimer()
}

/** The single reset action: back to "scan your card" and clear everything. */
function resetToScan(): void {
  stopIdleTimer()
  results.value = []
  quantities.value = {}
  cellWarning.value = ''
  saveError.value = ''
  loginError.value = ''
  employeeCode.value = ''
  codeMode.value = false
  worker.value = null
  activeProduct.value = null
  search.value = ''
  // The overlay must not survive the logout it is covering.
  journalOpen.value = false
  closeNumpad()
  phase.value = 'idle'
  void loadQueue()
  focusCard()
}

function focusCard(): void {
  void nextTick(() => cardInput.value?.focus())
}

// --- config --------------------------------------------------------------

function loadConfig(): void {
  const stored = readStored<Partial<KioskConfig> & { stage_code?: unknown } | null>(CONFIG_KEY, null)
  config.value = {
    // A legacy single `stage_code` is intentionally NOT carried over: it was the
    // hardcoded "knitting" default that never matched the orders' own codes.
    stage_codes: Array.isArray(stored?.stage_codes)
      ? stored.stage_codes.filter((code): code is string => typeof code === 'string' && code.trim() !== '')
      : [],
    warehouse_agent_id:
      typeof stored?.warehouse_agent_id === 'number' ? stored.warehouse_agent_id : null,
    token: typeof stored?.token === 'string' && stored.token.trim() ? stored.token.trim() : DEFAULT_KIOSK_TOKEN,
    idle_timeout_s: clampKioskIdleTimeout(stored?.idle_timeout_s),
  }
  stageCodeInput.value = config.value.stage_codes.join(', ')
}

function applyConfig(): void {
  config.value.stage_codes = parseStageCodes(stageCodeInput.value)
  config.value.token = config.value.token.trim() || DEFAULT_KIOSK_TOKEN
  config.value.idle_timeout_s = clampKioskIdleTimeout(config.value.idle_timeout_s)
  stageCodeInput.value = config.value.stage_codes.join(', ')
  writeStored(CONFIG_KEY, config.value)
}

function closeSetup(): void {
  applyConfig()
  setupOpen.value = false
  stageCodesDiscoveredAt = 0
  bumpIdleTimer()
  if (phase.value !== 'idle') void loadQueue()
}

async function loadWarehouses(): Promise<void> {
  try {
    warehouses.value = await listSemiFinishedWarehouses()
  } catch {
    warehouses.value = []
  }
  const ids = warehouses.value.map((warehouse) => warehouse.id)
  if (config.value.warehouse_agent_id !== null && !ids.includes(config.value.warehouse_agent_id)) {
    config.value.warehouse_agent_id = null
  }
  // Single terminal warehouse: configure it silently so the worker never sees setup.
  if (config.value.warehouse_agent_id === null && warehouses.value.length === 1) {
    config.value.warehouse_agent_id = warehouses.value[0].id
    applyConfig()
  }
}

/**
 * Detect the stage codes this terminal must consume.
 *
 * There is no shared "knitting" code: each production order holds the stage
 * codes of its own BOM, so the codes are read from the open orders' stages and
 * only the FIRST operation of every order is used (a knitting terminal must not
 * pick up downstream work). A manual override in the terminal setup wins.
 */
async function resolveStageCodes(): Promise<string[]> {
  const manual = config.value.stage_codes
  const cacheFresh = Date.now() - stageCodesDiscoveredAt < STAGE_CACHE_MS

  if (!cacheFresh || !autoStageCodes.value.length) {
    const byOrder: Record<string, string[]> = {}
    const detected = new Set<string>()
    try {
      const orders = await listCurrentProductionOrders()
      const results = await Promise.allSettled(
        orders.slice(0, STAGE_ORDER_SCAN_LIMIT).map(async (order) => ({
          orderId: order.id,
          stages: await listStages(order.id),
        })),
      )
      for (const result of results) {
        if (result.status !== 'fulfilled') continue
        const codes = firstOperationStageCodes(
          result.value.stages.map((stage) => ({
            stage_code: stage.stage_code,
            operation_sequence: stage.operation_sequence,
          })),
        )
        if (!codes.length) continue
        byOrder[String(result.value.orderId)] = codes
        codes.forEach((code) => detected.add(code))
      }
      autoStageCodes.value = [...detected].sort()
      firstStageByOrder.value = byOrder
      stageCodesDiscoveredAt = Date.now()
    } catch {
      // Keep the last known codes; the empty-state hint explains the situation.
    }
  }

  stageCodes.value = manual.length ? manual : autoStageCodes.value
  return stageCodes.value
}

/** Walk every page of the work queue for one stage code. */
async function fetchStageQueue(stageCode: string): Promise<WorkQueueItem[]> {
  const collected: WorkQueueItem[] = []
  let page = 1
  let totalPages = 1
  do {
    const response = await listWorkQueue({ stage_code: stageCode, page, per_page: 100 })
    collected.push(...response.items)
    totalPages = Math.max(1, response.meta.total_pages)
    page += 1
  } while (page <= totalPages && page <= 20)
  return collected
}

async function loadQueue(): Promise<void> {
  queueLoading.value = true
  queueError.value = ''
  try {
    const codes = await resolveStageCodes()
    if (!codes.length) {
      queue.value = []
      return
    }
    const answers = await Promise.allSettled(codes.map((code) => fetchStageQueue(code)))
    const lists = answers
      .filter((answer): answer is PromiseFulfilledResult<WorkQueueItem[]> => answer.status === 'fulfilled')
      .map((answer) => answer.value)
    if (!lists.length) {
      const failure = answers.find((answer) => answer.status === 'rejected')
      if (failure && failure.status === 'rejected') {
        queue.value = []
        queueError.value = formatApiError(failure.reason)
        return
      }
    }
    queue.value = firstStageOnlyRows(mergeWorkQueue(lists), firstStageByOrder.value)
  } catch (cause) {
    queue.value = []
    queueError.value = formatApiError(cause)
  } finally {
    queueLoading.value = false
  }
}

// --- worker login --------------------------------------------------------

function applyWorker(next: KioskWorker, nextShift: string): void {
  worker.value = next
  shift.value = nextShift === 'night' ? 'night' : 'day'
  loginError.value = ''
  cardUid.value = ''
  employeeCode.value = ''
  results.value = []
  quantities.value = {}
  saveError.value = ''
  activeProduct.value = null
  phase.value = 'product'
  void loadQueue()
  bumpIdleTimer()
}

async function submitCard(): Promise<void> {
  const value = cardUid.value.trim()
  if (!value || loginBusy.value) return
  loginError.value = ''
  loginBusy.value = true
  try {
    // Readers differ in zero padding, so the exact uid is tried first and the
    // unpadded form only when the card was simply not found (never on a
    // rejected device token or a network error).
    let lastError: unknown = null
    for (const candidate of kioskCardUidVariants(value)) {
      try {
        const response = await workerCardLogin({ card_uid: candidate }, config.value.token)
        applyWorker(
          {
            id: response.employee.id,
            name: response.employee.name,
            code: response.employee.employee_code,
            subtitle: response.department?.name ?? '',
            photoUrl: response.employee.photo_url,
          },
          response.shift,
        )
        return
      } catch (cause) {
        lastError = cause
        if (errorStatus(cause) !== 422) break
      }
    }

    const status = errorStatus(lastError)
    if (status === 401) {
      // The device token was rejected (unknown/replaced kiosk token).
      loginError.value = t('shopfloor.knittingKiosk.tokenRejected')
      codeMode.value = true
      void loadEmployees()
      void nextTick(() => codeInput.value?.focus())
    } else if (status === 422) {
      loginError.value = t('shopfloor.knittingKiosk.loginFailed')
    } else {
      loginError.value = formatApiError(lastError)
    }
    cardUid.value = ''
  } finally {
    loginBusy.value = false
    if (phase.value === 'idle' && !codeMode.value) focusCard()
  }
}

async function loadEmployees(): Promise<void> {
  if (employees.value.length) return
  try {
    employees.value = await listEmployees()
  } catch {
    employees.value = []
  }
}

async function submitCode(): Promise<void> {
  const value = employeeCode.value.trim().toLowerCase()
  if (!value) return
  loginError.value = ''
  await loadEmployees()
  const match = employees.value.find((employee) => (employee.employee_code ?? '').toLowerCase() === value)
  if (!match) {
    loginError.value = t('shopfloor.knittingKiosk.codeFailed')
    return
  }
  applyWorker(
    {
      id: match.id,
      name: `${match.first_name ?? ''} ${match.last_name ?? ''}`.trim() || match.employee_code,
      code: match.employee_code,
      subtitle: match.job_title ?? '',
      photoUrl: null,
    },
    shiftForNow(),
  )
}

function toggleCodeMode(): void {
  codeMode.value = !codeMode.value
  loginError.value = ''
  if (codeMode.value) {
    void loadEmployees()
    void nextTick(() => codeInput.value?.focus())
  } else {
    focusCard()
  }
}

// --- RFID / keyboard-wedge scan (login step) -----------------------------

let rfidCapture = newRfidCapture()

/** True for real form fields, where the input's own submit already runs. */
function isTypingTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null
  if (!element) return false
  return (
    element.tagName === 'INPUT' ||
    element.tagName === 'TEXTAREA' ||
    element.tagName === 'SELECT' ||
    element.isContentEditable
  )
}

/**
 * Accept a card scan wherever the screen has focus. Only active on the login
 * step with no dialog open; keystrokes typed into a field are ignored so a card
 * can never be submitted twice.
 */
function onRfidKeydown(event: KeyboardEvent): void {
  if (phase.value !== 'idle' || loginBusy.value || setupOpen.value || journalOpen.value) return
  if (isTypingTarget(event.target)) return

  const result = rfidKey(rfidCapture, event.key, Date.now())
  rfidCapture = result.capture
  if (result.scanned === null) return

  cardUid.value = result.scanned
  void submitCard()
}

function printTicket(): void {
  window.print()
}

onMounted(async () => {
  document.body.classList.add('shopfloor-kiosk-mode')
  clockTimer = window.setInterval(() => {
    now.value = new Date()
  }, CLOCK_TICK_MS)
  for (const eventName of ACTIVITY_EVENTS) {
    window.addEventListener(eventName, bumpIdleTimer, { passive: true })
  }
  // The matrix arrows depend on its width, which changes with the viewport.
  window.addEventListener('resize', updateMatrixScroll, { passive: true })
  // Escape (or the browser chrome) may leave fullscreen at any time.
  document.addEventListener('fullscreenchange', syncFullscreen)
  syncFullscreen()
  // Card scans are captured at window level: the reader does not need the input.
  window.addEventListener('keydown', onRfidKeydown)
  loadConfig()
  outbox.value = readStored<OutboxEntry[]>(OUTBOX_KEY, [])
  journal.value = kioskRecentJournal(readStored<JournalEntry[]>(JOURNAL_KEY, []), { now: new Date() })
  void loadJournal()
  focusCard()
  await loadWarehouses()
  void loadQueue()
  if (outbox.value.length) void flushOutbox()
})

onUnmounted(() => {
  document.body.classList.remove('shopfloor-kiosk-mode')
  stopIdleTimer()
  for (const eventName of ACTIVITY_EVENTS) {
    window.removeEventListener(eventName, bumpIdleTimer)
  }
  window.removeEventListener('resize', updateMatrixScroll)
  window.removeEventListener('keydown', onRfidKeydown)
  document.removeEventListener('fullscreenchange', syncFullscreen)
  if (clockTimer !== null) {
    window.clearInterval(clockTimer)
    clockTimer = null
  }
})
</script>

<template>
  <div class="kiosk">
    <header class="kiosk-bar">
      <div class="kiosk-brand">
        <p class="kiosk-brand__eyebrow">{{ dateLabel }}</p>
        <h2 class="kiosk-brand__title">{{ clockLabel }}</h2>
      </div>
      <div class="kiosk-bar__actions">
        <!-- Worker chip + shift live in the bar so the body stays for the work. -->
        <span v-if="worker" class="worker-chip">
          <span class="worker-chip__avatar">
            <img v-if="worker.photoUrl" :src="worker.photoUrl" :alt="worker.name" />
            <span v-else aria-hidden="true">{{ (worker.name || '?').charAt(0) }}</span>
          </span>
          <span class="worker-chip__meta">
            <strong>{{ worker.name }}</strong>
            <small>{{ worker.code }}<template v-if="worker.subtitle"> · {{ worker.subtitle }}</template></small>
          </span>
          <button
            type="button"
            class="worker-chip__action"
            :title="t('shopfloor.knittingKiosk.changeWorker')"
            :aria-label="t('shopfloor.knittingKiosk.changeWorker')"
            @click="resetToScan"
          >
            <X :size="14" aria-hidden="true" />
          </button>
        </span>

        <div v-if="worker" class="shift-toggle" role="group" :aria-label="t('shopfloor.knittingKiosk.shift')">
          <button
            v-for="option in shifts"
            :key="option"
            type="button"
            class="shift-toggle__btn"
            :class="{ active: shift === option }"
            :aria-pressed="shift === option"
            :aria-label="option === 'day' ? t('shopfloor.knittingKiosk.shiftDay') : t('shopfloor.knittingKiosk.shiftNight')"
            :title="option === 'day' ? t('shopfloor.knittingKiosk.shiftDay') : t('shopfloor.knittingKiosk.shiftNight')"
            @click="shift = option"
          >
            <Sun v-if="option === 'day'" :size="15" aria-hidden="true" />
            <Moon v-else :size="15" aria-hidden="true" />
            <span class="shift-toggle__label">{{ SHIFT_CAPTIONS[option] }}</span>
          </button>
        </div>

        <button
          v-if="worker"
          type="button"
          class="icon-btn"
          :title="t('shopfloor.knittingKiosk.journal')"
          :aria-label="t('shopfloor.knittingKiosk.journal')"
          @click="openJournal"
        >
          <History :size="20" aria-hidden="true" />
        </button>

        <span v-if="warehouseName" class="kiosk-chip">{{ warehouseName }}</span>
        <span
          v-if="worker && idleCountdown"
          class="kiosk-chip kiosk-chip--timer"
          :class="{ 'kiosk-chip--timer-low': idleSeconds !== null && idleSeconds <= 5 }"
          :title="t('shopfloor.knittingKiosk.sessionTimeout', { time: idleCountdown })"
        >
          <Timer :size="14" aria-hidden="true" />
          {{ idleCountdown }}
        </span>
        <button
          v-if="canFullscreen"
          type="button"
          class="icon-btn"
          :title="isFullscreen ? t('shopfloor.knittingKiosk.fullscreenExit') : t('shopfloor.knittingKiosk.fullscreenEnter')"
          :aria-label="isFullscreen ? t('shopfloor.knittingKiosk.fullscreenExit') : t('shopfloor.knittingKiosk.fullscreenEnter')"
          @click="toggleFullscreen"
        >
          <Minimize2 v-if="isFullscreen" :size="18" aria-hidden="true" />
          <Maximize2 v-else :size="18" aria-hidden="true" />
        </button>
        <button
          type="button"
          class="icon-btn"
          :title="t('shopfloor.knittingKiosk.setupOpen')"
          :aria-label="t('shopfloor.knittingKiosk.setupOpen')"
          @click="openSetup"
        >
          <Settings :size="20" aria-hidden="true" />
        </button>

        <!-- Way out of the terminal, back to the shopfloor application. -->
        <RouterLink
          to="/"
          class="icon-btn kiosk-exit"
          :title="t('shopfloor.knittingKiosk.backToShopfloor')"
          :aria-label="t('shopfloor.knittingKiosk.backToShopfloor')"
        >
          <Home :size="18" aria-hidden="true" />
        </RouterLink>
      </div>
    </header>

    <div v-if="pendingCount" class="alert alert--warn pending-banner">
      <div class="pending-banner__head">
        <div>
          <strong>{{ t('shopfloor.knittingKiosk.pendingTitle') }}</strong>
          <p>{{ t('shopfloor.knittingKiosk.pendingHint', { count: pendingCount }) }}</p>
        </div>
        <button type="button" class="ghost-btn" :disabled="saving" @click="sendQueued({ force: true })">
          <RefreshCw :size="16" aria-hidden="true" />
          {{ t('shopfloor.knittingKiosk.pendingRetry') }}
        </button>
      </div>

      <ul class="pending-list">
        <li v-for="entry in outbox" :key="entry.client_event_id" class="pending-item">
          <span class="pending-item__label">
            <strong>{{ entry.product_name || '—' }}</strong>
            <small>{{ t('shopfloor.knittingKiosk.pendingRows', { count: entry.payload.rows.length }) }}</small>
          </span>
          <span class="pending-item__quantity">{{ pendingQuantity(entry) }}</span>
          <span
            class="pending-item__status"
            :class="{ 'pending-item__status--locked': entry.status === 'needs_review' }"
          >
            {{
              entry.status === 'needs_review'
                ? t('shopfloor.knittingKiosk.pendingNeedsReview')
                : entry.error || t('shopfloor.knittingKiosk.pendingWaiting')
            }}
          </span>
          <button
            type="button"
            class="pending-item__cancel"
            :title="t('shopfloor.knittingKiosk.pendingCancel')"
            :aria-label="t('shopfloor.knittingKiosk.pendingCancel')"
            @click="cancelOutboxEntry(entry.client_event_id)"
          >
            <X :size="14" aria-hidden="true" />
          </button>
        </li>
      </ul>
    </div>

    <main class="kiosk-body">
      <!--
        One panel for every step: the panel head carries the instruction and the
        tools of the current step, the body below carries its content.
      -->
      <section class="panel">
        <div class="panel-head">
          <div class="panel-head__instruction">
            <h3 class="panel-instruction">{{ stepInstruction }}</h3>
            <p v-if="stepHint" class="panel-hint">{{ stepHint }}</p>
          </div>
          <div class="panel-head__tools">
            <template v-if="phase === 'product'">
              <input
                v-model="search"
                class="search-input"
                type="search"
                :placeholder="t('shopfloor.knittingKiosk.productsSearch')"
                :aria-label="t('shopfloor.knittingKiosk.productsSearch')"
              />
              <button type="button" class="ghost-btn" :disabled="queueLoading" @click="loadQueue">
                <RefreshCw :size="16" aria-hidden="true" />
                {{ t('shopfloor.knittingKiosk.productsRefresh') }}
              </button>
            </template>
            <template v-else-if="phase === 'matrix'">
              <button type="button" class="ghost-btn" @click="backToProducts">
                <ArrowLeft :size="16" aria-hidden="true" />
                {{ t('shopfloor.knittingKiosk.back') }}
              </button>
              <button type="button" class="ghost-btn" @click="clearAll">
                {{ t('shopfloor.knittingKiosk.matrixClear') }}
              </button>
            </template>
          </div>
        </div>

        <!-- 1) Idle — the card reader is armed (scan or type the card number) -->
        <div v-if="phase === 'idle'" class="scan-block">
          <form class="scan-block__form" @submit.prevent="submitCard">
            <span class="scan-block__icon" aria-hidden="true"><ScanLine :size="52" /></span>
            <label class="scan-field">
              <span>{{ t('shopfloor.knittingKiosk.cardLabel') }}</span>
              <input
                ref="cardInput"
                v-model="cardUid"
                type="text"
                autocomplete="off"
                :disabled="loginBusy"
                :placeholder="t('shopfloor.knittingKiosk.cardPlaceholder')"
              />
            </label>
            <button type="submit" class="big-btn" :disabled="loginBusy || !cardUid.trim()">
              {{ loginBusy ? t('shopfloor.knittingKiosk.loginChecking') : t('shopfloor.knittingKiosk.loginSubmit') }}
            </button>
          </form>

          <p v-if="loginError" class="alert alert--error">{{ loginError }}</p>

          <button type="button" class="link-btn" @click="toggleCodeMode">
            {{ t('shopfloor.knittingKiosk.codeToggle') }}
          </button>

          <form v-if="codeMode" class="scan-form" @submit.prevent="submitCode">
            <label class="scan-field">
              <span>{{ t('shopfloor.knittingKiosk.codeLabel') }}</span>
              <input
                ref="codeInput"
                v-model="employeeCode"
                type="text"
                autocomplete="off"
                :placeholder="t('shopfloor.knittingKiosk.codePlaceholder')"
              />
            </label>
            <button type="submit" class="big-btn" :disabled="!employeeCode.trim()">
              {{ t('shopfloor.knittingKiosk.codeSubmit') }}
            </button>
          </form>
        </div>

        <!-- 2) Product picker (product name + colours only) -->
        <template v-else-if="phase === 'product'">
          <p v-if="queueLoading" class="state-line">{{ t('shopfloor.state.loading') }}</p>
          <p v-else-if="queueError" class="alert alert--error">{{ queueError }}</p>
          <template v-else-if="!filteredProducts.length">
            <p class="state-line">
              {{ search ? t('shopfloor.knittingKiosk.productsNoMatch') : t('shopfloor.knittingKiosk.productsEmpty') }}
            </p>
            <p v-if="!search" class="state-hint">
              {{ t('shopfloor.knittingKiosk.productsEmptyHint', { stages: stageSummary }) }}
            </p>
          </template>

          <div v-else class="product-grid">
            <button
              v-for="group in filteredProducts"
              :key="group.orderId"
              type="button"
              class="product-card"
              @click="openProduct(group)"
            >
              <span class="product-card__name">{{ group.productName }}</span>
              <span v-if="group.subtitle" class="product-card__sub">{{ group.subtitle }}</span>
              <span class="product-card__colors">
                <span v-for="colour in group.colors.slice(0, 4)" :key="colour" class="colour-chip">{{ colour }}</span>
                <span v-if="group.colors.length > 4" class="colour-chip">+{{ group.colors.length - 4 }}</span>
              </span>
              <span class="product-card__foot">
                <span>
                  {{ t('shopfloor.knittingKiosk.productsRemaining') }}
                  <strong>{{ group.remaining.toLocaleString() }}</strong>
                </span>
                <span>{{ t('shopfloor.knittingKiosk.productsCells', { count: group.cells }) }}</span>
              </span>
            </button>
          </div>
        </template>

        <!-- 3) Colour / size matrix of the picked product -->
        <template v-else-if="phase === 'matrix'">
          <!-- The picked product, in large type above its input matrix. -->
          <p class="matrix-product">{{ activeProduct?.productName }}</p>
          <div class="matrix-wrap">
            <div ref="matrixScroll" class="matrix-scroll" @scroll.passive="updateMatrixScroll">
              <table class="matrix">
                <thead>
                  <tr>
                    <th class="matrix__corner">{{ t('shopfloor.knittingKiosk.matrixSize') }}</th>
                    <th v-for="colour in matrix.colors" :key="colour" class="matrix__colour">{{ colour }}</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="line in matrix.rows" :key="line.size">
                    <tr>
                      <th class="matrix__size">{{ line.size }}</th>
                      <td
                        v-for="cell in line.cells"
                        :key="cell.color"
                        class="matrix__cell"
                        :class="{
                          'matrix__cell--void': !cell.workItemId,
                          'matrix__cell--active': numpadCell === cell.workItemId,
                        }"
                      >
                        <template v-if="cell.workItemId">
                          <input
                            class="matrix__input"
                            type="text"
                            :inputmode="isTouchTerminal() ? 'none' : 'decimal'"
                            :value="quantities[cell.workItemId] ?? ''"
                            :aria-label="`${line.size} ${cell.color}`"
                            @input="onQuantityInput(cell.workItemId, $event)"
                            @focus="onQuantityFocus(cell.workItemId)"
                          />
                          <small class="matrix__hint">
                            {{ t('shopfloor.knittingKiosk.matrixLeft') }} {{ cell.remaining }}
                          </small>
                        </template>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>

            <!-- Swipe hints: only shown while that side still has more columns. -->
            <button
              v-if="matrixScrollState.left"
              type="button"
              class="matrix-arrow matrix-arrow--left"
              :aria-label="t('shopfloor.knittingKiosk.matrixScrollLeft')"
              :title="t('shopfloor.knittingKiosk.matrixScrollLeft')"
              @click="scrollMatrix(-1)"
            >
              <ChevronLeft :size="22" aria-hidden="true" />
            </button>
            <button
              v-if="matrixScrollState.right"
              type="button"
              class="matrix-arrow matrix-arrow--right"
              :aria-label="t('shopfloor.knittingKiosk.matrixScrollRight')"
              :title="t('shopfloor.knittingKiosk.matrixScrollRight')"
              @click="scrollMatrix(1)"
            >
              <ChevronRight :size="22" aria-hidden="true" />
            </button>
          </div>

          <p v-if="cellWarning" class="alert alert--warn">{{ cellWarning }}</p>
          <p v-if="saveError" class="alert alert--error">{{ saveError }}</p>
          <p v-if="!canManage" class="alert alert--warn">{{ t('shopfloor.knittingKiosk.needPermission') }}</p>

          <div class="matrix-footer">
            <span class="matrix-total">
              {{ t('shopfloor.knittingKiosk.matrixTotal') }}
              <strong>{{ totalOutput }}</strong>
            </span>
            <button
              type="button"
              class="big-btn"
              :disabled="saving || !canManage || !entries.length"
              @click="saveOutput"
            >
              {{ saving ? t('shopfloor.knittingKiosk.matrixSaving') : t('shopfloor.knittingKiosk.matrixSave') }}
            </button>
          </div>

          <!-- Number pad of the opened cell, centred over the screen -->
          <div v-if="numpadCell" class="numpad-modal" @click.self="closeNumpad">
            <div class="numpad" role="dialog" aria-modal="true" :aria-label="t('shopfloor.knittingKiosk.numpadOpen')">
              <div class="numpad__head">
                <span class="numpad__target">
                  <strong>{{ numpadTarget?.size }}</strong>
                  <span>{{ numpadTarget?.colour }}</span>
                  <span class="numpad__left">
                    {{ t('shopfloor.knittingKiosk.matrixLeft') }} {{ numpadTarget?.remaining }}
                  </span>
                </span>
                <button
                  type="button"
                  class="numpad__close"
                  :aria-label="t('shopfloor.knittingKiosk.numpadClose')"
                  @click="closeNumpad"
                >
                  <X :size="18" aria-hidden="true" />
                </button>
              </div>
              <p class="numpad__value">{{ quantities[numpadCell] ?? '0' }}</p>
              <div class="numpad__grid">
                <template v-for="rowKeys in numpadKeyRows" :key="rowKeys.join('-')">
                  <button
                    v-for="key in rowKeys"
                    :key="key"
                    type="button"
                    class="numpad__key"
                    :aria-label="numpadKeyLabel(key)"
                    @click="numpadPress(key)"
                  >
                    <Delete v-if="key === 'back'" :size="24" aria-hidden="true" />
                    <span v-else>{{ key }}</span>
                  </button>
                </template>
              </div>
              <div class="numpad__footer">
                <button type="button" class="numpad__key numpad__key--function" @click="numpadPress('clear')">
                  {{ t('shopfloor.knittingKiosk.matrixClear') }}
                </button>
                <button type="button" class="numpad__key numpad__key--done" @click="closeNumpad">
                  {{ t('shopfloor.knittingKiosk.numpadClose') }}
                </button>
              </div>
            </div>
          </div>
        </template>

        <!-- 4) Basket sticker — the code to write on the label of the basket -->
        <template v-else>
          <div class="ticket-list">
            <article v-for="basket in results" :key="basket.basket_code" class="ticket">
              <div class="ticket__batch">
                <span class="ticket__batch-label">{{ t('shopfloor.knittingKiosk.resultBasket') }}</span>
                <strong class="ticket__batch-value">{{ basket.basket_code }}</strong>
              </div>
              <ul v-if="basket.lines?.length" class="ticket__lines">
                <li v-for="line in basket.lines" :key="line.work_item_id" class="ticket__line">
                  <span class="ticket__line-label">
                    {{ line.color_name ?? '—' }} · {{ line.size_label ?? '—' }}
                  </span>
                  <span class="ticket__line-qty">{{ line.completed_quantity }}</span>
                  <span class="ticket__line-lot">{{ line.lot_number ?? '—' }}</span>
                </li>
              </ul>
            </article>
          </div>

          <p v-if="saveError" class="alert alert--error">{{ saveError }}</p>

          <div class="result-actions">
            <button type="button" class="big-btn" @click="backToProducts">
              <ArrowLeft :size="18" aria-hidden="true" />
              {{ t('shopfloor.knittingKiosk.resultBackToOrders') }}
            </button>
            <button type="button" class="ghost-btn" @click="resetToScan">
              <Check :size="18" aria-hidden="true" />
              {{ t('shopfloor.knittingKiosk.resultDone') }}
            </button>
            <button type="button" class="ghost-btn" @click="printTicket">
              <Printer :size="18" aria-hidden="true" />
              {{ t('shopfloor.knittingKiosk.resultPrint') }}
            </button>
          </div>
        </template>
      </section>
    </main>

    <!-- Journal: baskets this terminal saved in the last 24 hours -->
    <div v-if="journalOpen" class="setup-backdrop" @click.self="journalOpen = false">
      <section
        class="setup-panel journal-panel"
        role="dialog"
        aria-modal="true"
        :aria-label="t('shopfloor.knittingKiosk.journalTitle')"
      >
        <header class="setup-panel__head">
          <div>
            <h3>{{ t('shopfloor.knittingKiosk.journalTitle') }}</h3>
            <p>{{ t('shopfloor.knittingKiosk.journalSubtitle') }}</p>
          </div>
          <button
            type="button"
            class="icon-btn"
            :aria-label="t('shopfloor.knittingKiosk.journalClose')"
            @click="journalOpen = false"
          >
            <X :size="20" aria-hidden="true" />
          </button>
        </header>

        <div class="journal-filters" role="group">
          <button
            type="button"
            class="ghost-btn"
            :class="{ 'ghost-btn--active': journalFilter === 'mine' }"
            @click="journalFilter = 'mine'"
          >
            {{ t('shopfloor.knittingKiosk.journalMine') }}
          </button>
          <button
            type="button"
            class="ghost-btn"
            :class="{ 'ghost-btn--active': journalFilter === 'all' }"
            @click="journalFilter = 'all'"
          >
            {{ t('shopfloor.knittingKiosk.journalAll') }}
          </button>
        </div>

        <p class="journal-summary">
          {{ t('shopfloor.knittingKiosk.journalSummary', { baskets: journalTotals.baskets, quantity: journalTotals.quantity }) }}
        </p>

        <p v-if="journalLoading" class="journal-summary">{{ t('shopfloor.state.loading') }}</p>
        <p v-else-if="journalOffline" class="alert alert--warn">
          {{ t('shopfloor.knittingKiosk.journalOffline') }}
        </p>

        <p v-if="!recentJournal.length" class="journal-empty">
          {{ t('shopfloor.knittingKiosk.journalEmpty') }}
        </p>

        <template v-else>
          <div class="table-scroll">
            <table class="journal-table">
              <thead>
                <tr>
                  <th>{{ t('shopfloor.knittingKiosk.journalTime') }}</th>
                  <th>{{ t('shopfloor.knittingKiosk.resultBasket') }}</th>
                  <th>{{ t('shopfloor.knittingKiosk.journalProduct') }}</th>
                  <th v-if="journalFilter === 'all'">{{ t('shopfloor.knittingKiosk.journalWorker') }}</th>
                  <th>{{ t('shopfloor.knittingKiosk.journalLines') }}</th>
                  <th class="num">{{ t('shopfloor.knittingKiosk.journalQuantity') }}</th>
                  <th>{{ t('shopfloor.knittingKiosk.journalStatus') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in journalPaged.items" :key="`${row.basketCode}-${row.savedAt}`">
                  <td class="journal-table__time">{{ journalTime(row.savedAt) }}</td>
                  <td class="journal-table__code">{{ row.basketCode }}</td>
                  <td>{{ row.productName || '—' }}</td>
                  <td v-if="journalFilter === 'all'">{{ row.employeeName || '—' }}</td>
                  <td class="journal-table__lines" :title="kioskJournalLineLots(row)">
                    {{ kioskJournalLineSummary(row) || '—' }}
                  </td>
                  <td class="num">{{ row.totalQuantity }}</td>
                  <td><span class="journal-status">{{ row.status || '—' }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="journal-pager">
            <span class="journal-pager__label">
              {{
                t('shopfloor.knittingKiosk.journalPage', {
                  count: journalPaged.total,
                  page: journalPaged.page,
                  total: journalPaged.pageCount,
                })
              }}
            </span>
            <span class="journal-pager__buttons">
              <button
                type="button"
                class="ghost-btn"
                :disabled="journalPaged.page <= 1"
                @click="setJournalPage(journalPaged.page - 1)"
              >
                {{ t('shopfloor.pagination.previous') }}
              </button>
              <button
                type="button"
                class="ghost-btn"
                :disabled="journalPaged.page >= journalPaged.pageCount"
                @click="setJournalPage(journalPaged.page + 1)"
              >
                {{ t('shopfloor.pagination.next') }}
              </button>
            </span>
          </div>
        </template>
      </section>
    </div>

    <!-- Terminal setup (configured once per device) -->
    <div v-if="setupOpen" class="setup-backdrop" @click.self="closeSetup">
      <section class="setup-panel" role="dialog" aria-modal="true" :aria-label="t('shopfloor.knittingKiosk.setupTitle')">
        <header class="setup-panel__head">
          <div>
            <h3>{{ t('shopfloor.knittingKiosk.setupTitle') }}</h3>
            <p>{{ t('shopfloor.knittingKiosk.setupDescription') }}</p>
          </div>
          <button
            type="button"
            class="icon-btn"
            :aria-label="t('shopfloor.knittingKiosk.close')"
            @click="closeSetup"
          >
            <X :size="20" aria-hidden="true" />
          </button>
        </header>

        <label class="scan-field">
          <span>{{ t('shopfloor.knittingKiosk.stageCode') }}</span>
          <input v-model="stageCodeInput" type="text" autocomplete="off" :placeholder="t('shopfloor.knittingKiosk.stageCodePlaceholder')" />
          <small>{{ t('shopfloor.knittingKiosk.stageCodeHint') }}</small>
          <small>{{ t('shopfloor.knittingKiosk.stageDetected', { stages: autoStageSummary }) }}</small>
        </label>

        <label class="scan-field">
          <span>{{ t('shopfloor.knittingKiosk.token') }}</span>
          <input v-model="config.token" type="text" autocomplete="off" />
          <small>{{ t('shopfloor.knittingKiosk.tokenHint') }}</small>
        </label>

        <label class="scan-field">
          <span>{{ t('shopfloor.knittingKiosk.idleTimeout') }}</span>
          <input
            v-model.number="config.idle_timeout_s"
            type="number"
            inputmode="numeric"
            step="1"
            :min="KIOSK_IDLE_TIMEOUT_MIN_S"
            :max="KIOSK_IDLE_TIMEOUT_MAX_S"
            autocomplete="off"
          />
          <small>
            {{ t('shopfloor.knittingKiosk.idleTimeoutHint', { min: KIOSK_IDLE_TIMEOUT_MIN_S, max: KIOSK_IDLE_TIMEOUT_MAX_S }) }}
          </small>
        </label>

        <label class="scan-field">
          <span>{{ t('shopfloor.knittingKiosk.warehouse') }}</span>
          <select v-model.number="config.warehouse_agent_id">
            <option :value="null" disabled>—</option>
            <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">
              {{ warehouse.name }}
            </option>
          </select>
        </label>
        <p v-if="!warehouses.length" class="alert alert--error">
          {{ t('shopfloor.knittingKiosk.warehouseEmpty') }}
        </p>

        <div class="setup-panel__actions">
          <button type="button" class="big-btn" @click="closeSetup">
            {{ t('shopfloor.knittingKiosk.close') }}
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
/*
 * Dark terminal theme — the kiosk sits on the factory floor, often in low light,
 * and is left running for a whole shift. Palette:
 *   canvas #0f1416 · surface #161d20 · inner surface #1c2528
 *   border #2a3439 · border strong #38454b
 *   text #e7eef1 · muted #93a3a9
 *   accent (green) #3ea184 · accent hover #35876f · accent tint #16302a
 */
.kiosk {
  min-height: 100%;
  padding: 18px 18px 28px;
  background: #0f1416;
  color: #e7eef1;
}

/* --- top bar ------------------------------------------------------------ */
.kiosk-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-bottom: 14px;
}
.kiosk-brand {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.kiosk-brand__eyebrow {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #7c8b91;
}
.kiosk-brand__title {
  margin: 0;
  font-size: 30px;
  line-height: 1.1;
  /* Tabular digits: the clock must not jitter as the seconds change. */
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}
.kiosk-bar__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
}
.kiosk-chip {
  padding: 6px 12px;
  border-radius: 999px;
  background: #1c2528;
  border: 1px solid #2a3439;
  font-size: 12px;
  font-weight: 700;
  color: #a7b6bb;
}
.kiosk-chip--timer {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #cfe0da;
  font-variant-numeric: tabular-nums;
}
.kiosk-chip--timer-low {
  background: #3a2b16;
  border-color: #7a5a24;
  color: #f2c46b;
}
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 1px solid #2a3439;
  border-radius: 12px;
  background: #1c2528;
  color: #a7b6bb;
  cursor: pointer;
}
.icon-btn:hover {
  border-color: #3ea184;
  color: #7fe0c0;
}
/* RouterLink renders an <a>: keep the kiosk look and drop the link underline. */
.kiosk-exit {
  text-decoration: none;
}

/* --- panel head (step instruction + the tools of the current step) ----- */
.panel-head__instruction {
  min-width: 200px;
  flex: 1 1 280px;
}
/* Compact shift switch in the kiosk bar (buttons stay 44px elsewhere). */
.kiosk-bar .shift-toggle__btn {
  min-width: 0 !important;
  min-height: 0 !important;
  padding: 3px 9px !important;
  border-radius: 999px !important;
}
.worker-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 2px 4px 2px 2px;
  border: 1px solid #2a3439;
  border-radius: 999px;
  background: #1c2528;
}
/* X inside the chip: “change worker” without another button in the bar.
   The !important set defeats the 48px tablet touch-target rule. */
.worker-chip__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px !important;
  height: 26px !important;
  min-width: 26px !important;
  min-height: 26px !important;
  padding: 0 !important;
  border: 0;
  border-radius: 999px !important;
  background: #263034;
  color: #93a3a9;
  cursor: pointer;
}
.worker-chip__action:hover {
  background: #3a2220;
  color: #f0a899;
}
.worker-chip__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
  background: #16302a;
  color: #7fe0c0;
  font-size: 13px;
  font-weight: 700;
}
.worker-chip__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.worker-chip__meta {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
}
.worker-chip__meta strong {
  font-size: 13px;
}
.worker-chip__meta small {
  color: #93a3a9;
  font-size: 10px;
}
.shift-toggle {
  display: inline-flex;
  padding: 2px;
  gap: 2px;
  border-radius: 999px;
  background: #101719;
  border: 1px solid #2a3439;
}
.shift-toggle__btn {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  min-width: 0;
  min-height: 0;
  padding: 4px 10px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #a7b6bb;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
}
.shift-toggle__label {
  font-size: inherit;
  line-height: 1;
  letter-spacing: 0.02em;
}
.shift-toggle__btn.active {
  background: #3ea184;
  color: #08211b;
}

/* --- shared buttons ----------------------------------------------------- */
.big-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 56px;
  padding: 0 26px;
  border: 0;
  border-radius: 12px;
  background: #3ea184;
  color: #08211b;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
}
.big-btn:hover:not(:disabled) {
  background: #4db694;
}
.big-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.ghost-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  border: 1px solid #38454b;
  border-radius: 10px;
  background: #1c2528;
  color: #cfe0d9;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}
.ghost-btn:hover:not(:disabled) {
  border-color: #3ea184;
  color: #7fe0c0;
}
.ghost-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.link-btn {
  min-height: 44px;
  border: 0;
  background: none;
  color: #7fe0c0;
  font-size: 14px;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}

/* --- idle scan block ---------------------------------------------------- */
.scan-block {
  max-width: 640px;
  margin: 0 auto;
}
.scan-block__form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
/* Big reader badge, centred in the panel so the scan target is unmissable. */
.scan-block__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  width: 104px;
  height: 104px;
  margin: 6px 0 2px;
  border: 2px solid #2f5b4e;
  border-radius: 50%;
  background: #16302a;
  color: #4db694;
}
.scan-block .link-btn {
  margin-top: 8px;
}
.scan-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  margin-top: 12px;
}
.scan-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  color: #93a3a9;
  font-size: 12px;
  font-weight: 700;
}
.scan-field input,
.scan-field select {
  width: 100%;
  min-height: 54px;
  padding: 10px 14px;
  border: 1px solid #38454b;
  border-radius: 10px;
  background: #0f1416;
  color: #e7eef1;
  font-size: 17px;
}
.scan-field input::placeholder {
  color: #63737a;
}
.scan-field input:focus,
.scan-field select:focus {
  outline: none;
  border-color: #3ea184;
  box-shadow: 0 0 0 3px rgb(62 161 132 / 28%);
}
.scan-field small {
  font-weight: 500;
  color: #7c8b91;
}

/* --- product picker ---------------------------------------------------- */
.panel {
  padding: 18px;
  background: #161d20;
  border: 1px solid #2a3439;
  border-radius: 14px;
}
.panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
/* The instruction lives in the panel head; it must beat the generic `.panel-head h3`. */
.panel-head__instruction h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.25;
}
.panel-head__instruction p {
  margin: 1px 0 0;
  color: #7c8b91;
  font-size: 11px;
  line-height: 1.3;
}
.panel-head__tools {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.search-input {
  min-height: 44px;
  min-width: 240px;
  padding: 8px 14px;
  border: 1px solid #38454b;
  border-radius: 10px;
  background: #0f1416;
  color: #e7eef1;
  font-size: 15px;
}
.search-input::placeholder {
  color: #63737a;
}
.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 8px;
}
.product-card {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 0;
  padding: 8px 10px;
  border: 1px solid #2a3439;
  border-radius: 10px;
  background: #1c2528;
  color: #e7eef1;
  text-align: left;
  cursor: pointer;
}
.product-card:hover {
  border-color: #3ea184;
  box-shadow: 0 6px 18px rgb(0 0 0 / 45%);
}
.product-card__name {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
}
.product-card__sub {
  color: #7c8b91;
  font-size: 10px;
}
.product-card__colors {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}
.colour-chip {
  padding: 1px 6px;
  border-radius: 999px;
  background: #263034;
  color: #9adfc6;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.5;
}
.product-card__foot {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: auto;
  padding-top: 4px;
  border-top: 1px solid #2a3439;
  color: #93a3a9;
  font-size: 11px;
}
.product-card__foot strong {
  color: #7fe0c0;
  font-size: 13px;
}

/* --- matrix ------------------------------------------------------------ */
.matrix-product {
  margin: 0 0 12px;
  color: #e7eef1;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: 0.01em;
}
.matrix-wrap {
  position: relative;
  margin-bottom: 14px;
}
.matrix-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 2px;
}
/* max-content + fixed cell widths: many colours widen the table instead of
   squeezing every cell, and `.matrix-scroll` then scrolls horizontally. */
.matrix {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 6px;
}
/* Edge arrows hint that the matrix can be swiped horizontally. */
.matrix-arrow {
  position: absolute;
  top: 50%;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #3ea184;
  color: #08211b;
  box-shadow: 0 6px 16px rgb(0 0 0 / 45%);
  transform: translateY(-50%);
  cursor: pointer;
}
.matrix-arrow--left {
  /* Clear the pinned size column: the arrow only covers the colour cells. */
  left: 100px;
}
.matrix-arrow--right {
  right: 2px;
}
.matrix-arrow:hover {
  background: #4db694;
}
.matrix th {
  font-size: 12px;
  font-weight: 700;
  color: #b9c6cb;
}
/* Colour column labels — green, matching the terminal accent. */
.matrix__colour {
  padding: 6px 10px;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: #7fe0c0;
}
.matrix__corner,
.matrix__size {
  width: 88px;
  min-width: 88px;
  padding: 8px 10px;
  /* Pin the size column: only the colour cells scroll horizontally. */
  position: sticky;
  left: 0;
  z-index: 1;
  text-align: left;
  white-space: nowrap;
  background: #101719;
  border-radius: 8px;
  /* Covers the border-spacing gap so scrolling colour cells never show through. */
  box-shadow: 6px 0 0 #161d20;
}
.matrix__size {
  font-size: 22px;
  font-weight: 800;
  line-height: 1.2;
}
.matrix__cell {
  width: 176px;
  padding: 8px;
  vertical-align: top;
  border: 1px solid #2a3439;
  border-radius: 10px;
  background: #1c2528;
  text-align: center;
}
.matrix__cell--void {
  background: #121a1d;
  border-style: dashed;
  border-color: #232e33;
}
.matrix__cell--active {
  border-color: #3ea184;
  box-shadow: 0 0 0 2px rgb(62 161 132 / 25%);
}
/* Quantity input of a matrix cell. The number pad opens on focus on touch terminals. */
.matrix__input {
  width: 100%;
  min-height: 48px;
  padding: 6px 10px;
  border: 1px solid #38454b;
  border-radius: 8px;
  background: #0f1416;
  color: #e7eef1;
  font-size: 19px;
  font-weight: 700;
  text-align: center;
}
.matrix__input:focus {
  outline: none;
  border-color: #3ea184;
  box-shadow: 0 0 0 3px rgb(62 161 132 / 28%);
}
.matrix__hint {
  display: block;
  margin-top: 4px;
  color: #7c8b91;
  font-size: 11px;
  font-weight: 600;
}
/* --- on-screen number pad (touch terminals) ---------------------------- */
/* Centred modal: the pad never fights with the matrix scroll/table layout. */
.numpad-modal {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgb(6 10 11 / 72%);
}
.numpad {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: min(420px, 92vw);
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  padding: 16px;
  border: 1px solid #2a3439;
  border-radius: 16px;
  background: #131c1f;
  box-shadow: 0 24px 60px rgb(0 0 0 / 55%);
}
.numpad__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.numpad__target {
  display: flex;
  align-items: baseline;
  gap: 8px;
  color: #9fb0b6;
  font-size: 13px;
  font-weight: 700;
}
.numpad__target strong {
  color: #e7eef1;
  font-size: 18px;
}
.numpad__left {
  color: #7c8b91;
  font-weight: 600;
}
.numpad__value {
  margin: 0;
  padding: 10px 14px;
  border: 1px solid #38454b;
  border-radius: 10px;
  background: #0f1416;
  color: #7fe0c0;
  font-size: 34px;
  font-weight: 800;
  line-height: 1.1;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.numpad__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  min-width: 40px;
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: #223035;
  color: #cfe0da;
  cursor: pointer;
}
.numpad__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}
.numpad__key {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 62px;
  border: 0;
  border-radius: 12px;
  background: #223035;
  color: #e7eef1;
  font-size: 26px;
  font-weight: 800;
  cursor: pointer;
}
.numpad__key:active {
  background: #2f4348;
}
.numpad__footer {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}
.numpad__key--function {
  background: #1b262a;
  color: #9fb0b6;
  font-size: 15px;
  font-weight: 700;
}
.numpad__key--done {
  background: #3ea184;
  color: #08211b;
  font-size: 16px;
}
.matrix-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  padding-top: 14px;
  border-top: 1px solid #2a3439;
}
.matrix-total {
  font-size: 15px;
  color: #a7b6bb;
}
.matrix-total strong {
  margin-left: 6px;
  font-size: 24px;
  color: #7fe0c0;
}

/* --- result / sticker -------------------------------------------------- */
.ticket-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 14px;
}
.ticket {
  padding: 18px;
  border: 2px dashed #3ea184;
  border-radius: 12px;
  background: #12211c;
  color: #e7eef1;
}
.ticket__batch {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ticket__batch-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #93a3a9;
}
.ticket__batch-value {
  font-family: 'Courier New', ui-monospace, monospace;
  font-size: 30px;
  letter-spacing: 0.08em;
  color: #9ceccd;
  word-break: break-all;
}
.ticket__lines {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin: 10px 0 0;
  padding: 8px 0 0;
  border-top: 1px dashed #2f5b4e;
  list-style: none;
}
.ticket__line {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 12px;
}
.ticket__line-label {
  flex: 1 1 auto;
  color: #cfe0d9;
  font-weight: 600;
}
.ticket__line-qty {
  min-width: 40px;
  text-align: right;
  color: #e7eef1;
  font-weight: 700;
}
.ticket__line-lot {
  flex: 0 0 auto;
  font-family: 'Courier New', ui-monospace, monospace;
  font-size: 11px;
  color: #9ceccd;
}
.result-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 18px;
}

/* --- alerts / states --------------------------------------------------- */
.alert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  margin: 12px 0;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 14px;
}
.alert p {
  margin: 4px 0 0;
}
.alert--error {
  border: 1px solid #6d3129;
  background: #2a1614;
  color: #f0a899;
}
.alert--warn {
  border: 1px solid #6a5320;
  background: #2a2110;
  color: #e8c47c;
}
/* --- queued submissions banner ----------------------------------------- */
.pending-banner {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}
.pending-banner__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.pending-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.pending-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 8px;
  border: 1px solid #6a5320;
  border-radius: 8px;
  background: rgb(0 0 0 / 18%);
  font-size: 12px;
}
.pending-item__label {
  display: flex;
  flex-direction: column;
  min-width: 130px;
  line-height: 1.2;
}
.pending-item__label small {
  opacity: 0.75;
  font-size: 10px;
}
.pending-item__quantity {
  min-width: 44px;
  font-weight: 700;
  text-align: right;
}
.pending-item__status {
  flex: 1 1 auto;
  opacity: 0.85;
  overflow-wrap: anywhere;
}
.pending-item__status--locked {
  font-weight: 700;
}
.pending-item__cancel {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  border: 1px solid currentcolor;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  opacity: 0.8;
}
.pending-item__cancel:hover {
  opacity: 1;
}

.state-line {
  margin: 0;
  padding: 22px 0;  color: #93a3a9;
  font-size: 15px;
  text-align: center;
}
.state-hint {
  max-width: 620px;
  margin: -8px auto 8px;
  color: #7c8b91;
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
}

/* --- setup overlay ----------------------------------------------------- */
.setup-backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgb(4 8 9 / 72%);
}
.setup-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 460px;
  max-height: 86vh;
  overflow-y: auto;
  padding: 22px;
  border: 1px solid #2a3439;
  border-radius: 14px;
  background: #161d20;
  color: #e7eef1;
}
.setup-panel__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.setup-panel__head h3 {
  margin: 0 0 4px;
  font-size: 18px;
}
.setup-panel__head p {
  margin: 0;
  color: #93a3a9;
  font-size: 13px;
}
.setup-panel__actions {
  display: flex;
  justify-content: flex-end;
}

/* --- journal overlay --------------------------------------------------- */
.journal-panel {
  max-width: 760px;
}
.journal-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.ghost-btn--active {
  border-color: #3ea184 !important;
  background: #16302a !important;
  color: #7fe0c0 !important;
}
.journal-summary {
  margin: 0;
  color: #93a3a9;
  font-size: 12px;
}
.journal-empty {
  margin: 8px 0 0;
  padding: 18px 0;
  color: #93a3a9;
  font-size: 14px;
  text-align: center;
}
.journal-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.journal-table th {
  padding: 6px 8px;
  border-bottom: 1px solid #2a3439;
  color: #93a3a9;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}
.journal-table td {
  padding: 7px 8px;
  border-bottom: 1px solid #1f282c;
  vertical-align: top;
}
.journal-table__time {
  color: #a7b6bb;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.journal-table__code {
  font-family: 'Courier New', ui-monospace, monospace;
  font-size: 14px;
  font-weight: 700;
  color: #9ceccd;
  white-space: nowrap;
}
.journal-table__lines {
  max-width: 260px;
  color: #cfe0d9;
}
.journal-table .num {
  text-align: right;
  font-weight: 700;
  white-space: nowrap;
}
.journal-status {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: #263034;
  color: #b9c6cb;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.journal-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 10px;
}
.journal-pager__label {
  color: #93a3a9;
  font-size: 12px;
}
.journal-pager__buttons {
  display: flex;
  gap: 6px;
}

/* --- small tablets / portrait ------------------------------------------ */
@media (max-width: 760px) {
  .kiosk {
    padding: 12px 12px 22px;
  }
  .kiosk-bar__actions {
    width: 100%;
    justify-content: flex-start;
  }
  .panel-head__instruction h3 {
    font-size: 15px;
  }
  .panel-head__instruction p {
    display: none;
  }
  .kiosk-brand__title {
    font-size: 26px;
  }
  .product-grid {
    grid-template-columns: 1fr;
  }
  .panel-head__tools {
    width: 100%;
  }
  .search-input {
    flex: 1 1 100%;
    min-width: 0;
  }
  .matrix__cell {
    width: 150px;
  }
  .matrix-product {
    font-size: 22px;
  }
  .ticket-list {
    grid-template-columns: 1fr;
  }
  .matrix-footer .big-btn,
  .result-actions .big-btn {
    width: 100%;
  }
}

/* --- sticker printing -------------------------------------------------- */
@media print {
  .kiosk {
    padding: 0;
    background: #fff;
    color: #000;
  }
  .kiosk-bar,
  .panel-head,
  .alert,
  .result-actions {
    display: none !important;
  }
  .panel {
    border: 0;
    padding: 0;
    background: #fff;
  }
  .ticket-list {
    display: block;
  }
  /* Thermal/laser printers must not flood the label with the dark surface. */
  .ticket {
    margin-bottom: 14px;
    border: 1px solid #000;
    border-style: solid;
    background: #fff;
    color: #000;
    break-inside: avoid;
  }
  .ticket__batch-label,
  .ticket__line-label,
  .ticket__line-lot {
    color: #333;
  }
  .ticket__batch-value {
    color: #000;
  }
}

/*
 * Shared-theme override layer.
 *
 * `packages/ui/src/styles.css` forces the light workspace look for every app
 * with `!important` rules scoped to `.app-content`:
 *   - every `section` becomes a white card
 *   - `th` gets a light-blue strip and `td` pale borders
 *   - inputs/selects render as white controls
 *   - `button[type="submit"]` is painted with the app accent
 * The kiosk is a dark terminal, so each of those properties is re-asserted here.
 * These selectors are two classes deep (`.kiosk .x`) while the shared rules are
 * one class deep (`.app-content :where(...)`, `:where()` adds no specificity), so
 * with both sides using `!important` the kiosk wins.
 */
.kiosk .panel,
.kiosk .setup-panel {
  border-color: #2a3439 !important;
  background: #161d20 !important;
  box-shadow: none;
}
.kiosk .journal-table,
.kiosk .journal-table th,
.kiosk .journal-table td {
  background: transparent !important;
}
.kiosk .journal-table th {
  border-color: #2a3439 !important;
  color: #93a3a9 !important;
}
.kiosk .journal-table td {
  border-color: #1f282c !important;
}
.kiosk .journal-table tbody tr:hover {
  background: transparent !important;
}
.kiosk .product-card {
  border-color: #2a3439 !important;
  background: #1c2528 !important;
  /* tablet.css forces 48px min-height + 10px vertical padding on every button
     ≤1180px (the kiosk tablet range), which would inflate these small tiles. */
  min-height: 0 !important;
  padding: 8px 10px !important;
  border-radius: 10px !important;
  box-shadow: none;
}
.kiosk .product-card:hover {
  border-color: #3ea184 !important;
  box-shadow: 0 6px 18px rgb(0 0 0 / 45%);
}
.kiosk .matrix {
  background: transparent !important;
}
.kiosk .matrix th {
  border-color: #2a3439 !important;
  color: #b9c6cb !important;
  background: #101719 !important;
}
.kiosk .matrix__colour {
  color: #7fe0c0 !important;
}
.kiosk .colour-chip {
  color: #9adfc6 !important;
}
.kiosk .matrix__size {
  font-size: 22px !important;
}
.kiosk .matrix__corner,
.kiosk .matrix__size {
  border-color: #2a3439 !important;
  background: #101719 !important;
}
.kiosk .matrix__cell {
  border-color: #2a3439 !important;
  background: #1c2528 !important;
}
.kiosk .matrix__cell--void {
  border-color: #232e33 !important;
  background: #121a1d !important;
}
.kiosk .matrix tbody tr:hover {
  background: transparent !important;
}
.kiosk .matrix__input,
.kiosk .search-input,
.kiosk .scan-field input,
.kiosk .scan-field select {
  border-color: #38454b !important;
  border-radius: 10px !important;
  color: #e7eef1 !important;
  background: #0f1416 !important;
}
.kiosk .matrix__input {
  border-radius: 8px !important;
  font-size: 19px !important;
}
.kiosk .matrix__input:focus,
.kiosk .search-input:focus,
.kiosk .scan-field input:focus,
.kiosk .scan-field select:focus {
  border-color: #3ea184 !important;
  outline: 3px solid rgb(62 161 132 / 25%) !important;
}
/* Number pad: 4-class selectors + !important so tablet.css's
   `.app-content button:not(...)` touch rule (0,3,1) cannot flatten the keys. */
.kiosk .numpad-modal .numpad .numpad__grid .numpad__key {
  min-height: 62px !important;
  padding: 0 !important;
  border-radius: 12px !important;
  font-size: 26px !important;
  color: #e7eef1 !important;
  background: #223035 !important;
}
.kiosk .numpad-modal .numpad .numpad__grid .numpad__key:active {
  background: #2f4348 !important;
}
.kiosk .numpad-modal .numpad .numpad__footer .numpad__key--function {
  font-size: 15px !important;
  color: #9fb0b6 !important;
  background: #1b262a !important;
}
.kiosk .numpad-modal .numpad .numpad__footer .numpad__key--done {
  font-size: 16px !important;
  color: #08211b !important;
  background: #3ea184 !important;
}
.kiosk .numpad-modal .numpad .numpad__head .numpad__close {
  min-height: 40px !important;
  padding: 0 !important;
  border-radius: 10px !important;
  color: #cfe0da !important;
  background: #223035 !important;
}
.kiosk .numpad-modal .numpad .numpad__value {
  color: #7fe0c0 !important;
  font-size: 34px !important;
}
.kiosk .numpad-modal .numpad {
  border-color: #2a3439 !important;
  background: #131c1f !important;
}
.kiosk .matrix__cell--active {
  border-color: #3ea184 !important;
}
/* tablet.css / styles.css force 48px and a radius on every app button. */
.kiosk .matrix-wrap .matrix-arrow {
  width: 44px !important;
  min-width: 44px !important;
  height: 44px !important;
  min-height: 44px !important;
  padding: 0 !important;
  border: 0 !important;
  border-radius: 50% !important;
  background: #3ea184 !important;
  color: #08211b !important;
}
.kiosk .matrix-wrap .matrix-arrow:hover {
  background: #4db694 !important;
}
/* tablet.css/styles.css force 48px + a border radius on every app button. */
.kiosk .matrix-wrap .matrix-arrow {
  width: 44px !important;
  min-width: 44px !important;
  height: 44px !important;
  min-height: 44px !important;
  padding: 0 !important;
  border: 0 !important;
  border-radius: 50% !important;
  background: #3ea184 !important;
  color: #08211b !important;
}
.kiosk .matrix-wrap .matrix-arrow:hover {
  background: #4db694 !important;
}
.kiosk .scan-block button[type='submit'],
.kiosk .scan-form button[type='submit'] {
  border-color: #3ea184 !important;
  color: #08211b !important;
  background: #3ea184 !important;
}
.kiosk .scan-block button[type='submit']:hover:not(:disabled),
.kiosk .scan-form button[type='submit']:hover:not(:disabled) {
  background: #4db694 !important;
}
/* Keep the kiosk's own radii against the shared `border-radius: !important`. */
.kiosk .big-btn {
  border-radius: 12px !important;
}
.kiosk .shift-toggle__btn {
  border-radius: 999px !important;
  background: transparent !important;
  color: #a7b6bb !important;
}
.kiosk .shift-toggle__btn.active {
  background: #3ea184 !important;
  color: #08211b !important;
}
.kiosk .worker-chip,
.kiosk .colour-chip,
.kiosk .kiosk-chip {
  border-radius: 999px !important;
}
.kiosk .matrix-product {
  color: #e7eef1 !important;
}
</style>
